import { defineStore } from 'pinia';

/**
 * Returns the default state for filters.
 * @returns {Object} Default filter state object.
 */
function getDefaultState() {
    return {
        // defining an empty state for filters that
        // can be set through out application.
        // example of populated state could be
        // filters: {
        //      facilityId: {
        //       "cause": "", // stores search term for causes at facility level
        //       "causeCategory": "", // stores search term for cause category at facility level
        //       "assets": {
        //         assetId: {
        //           "layoutSettings": {...} // object that can store UI level settings for each asset.
        //           "causes": "", // stores search term for causes at asset level
        //           "products": "" // stores search term for products at asset level.
        //         }
        //       }
        //      }
        // }
        filters: {}
    };
}

/**
 * Initializes a facility in the state.
 * @returns {Object} Default facility filters object.
 */
function buildDefaultFacilityFilters() {
    return {
        assets: {}
    };
}

/**
 * Returns an empty object for initial filter structure.
 * @returns {Object} Empty filter structure object.
 */
function buildInitialFilterStructure() {
    return {};
}

/**
 * Pinia store for managing filters at facility, asset, and user account levels.
 * Provides state, actions, and getters for filter management.
 * @returns {Object} Pinia store instance for filters.
 */
export const useFiltersStore = defineStore('filters', {
    state: () => ({
        ...getDefaultState()
    }),

    getters: {
        /**
         * Gets the stored filter at asset settings level.
         * @param {Object} state - The store state.
         * @returns {function} Callable that returns the stored asset filter value.
         */
        getStoredAssetFilter:
            state =>
            ({ facilityId, assetId, filter }) => {
                const facilityFilter = state.filters[facilityId];
                if (
                    facilityFilter &&
                    facilityFilter['assets'] &&
                    facilityFilter['assets'][assetId] &&
                    facilityFilter['assets'][assetId][filter]
                ) {
                    return facilityFilter['assets'][assetId][filter];
                }
                return '';
            },

        /**
         * Gets the stored filter at facility settings level.
         * @param {Object} state - The store state.
         * @returns {function} Callable that returns the stored facility filter value.
         */
        getStoredFacilityFilter:
            state =>
            ({ facilityId, filter }) => {
                const facilityFilter = state.filters[facilityId];
                if (facilityFilter && facilityFilter[filter]) {
                    return facilityFilter[filter];
                }
                return '';
            },

        /**
         * Gets the stored filter at user account settings level.
         * @param {Object} state - The store state.
         * @returns {function} Callable that returns the stored user account filter value.
         */
        getStoredUserAccountFilter:
            state =>
            ({ filter }) => {
                const userAccountFilter = state.filters['userAccount'];
                if (userAccountFilter && userAccountFilter[filter]) {
                    return userAccountFilter[filter];
                }
                return '';
            }
    },

    actions: {
        /**
         * Resets the filter state to its original state.
         */
        resetState() {
            this.$reset();
        },

        /**
         * Stores a filter applied at the facility level.
         * @param {Object} params - Filter parameters.
         * @param {string} params.facilityId - Unique ID for the facility.
         * @param {string} params.filter - Filter key.
         * @param {string} params.value - Filter value.
         */
        setFacilityFilter({ facilityId, filter, value }) {
            if (!(facilityId in this.filters)) {
                this.filters[facilityId] = buildDefaultFacilityFilters();
            }
            this.filters[facilityId][filter] = value;
        },

        /**
         * Stores a filter applied at the asset level.
         * @param {Object} params - Filter parameters.
         * @param {string} params.facilityId - Unique ID for the facility.
         * @param {string} params.assetId - Unique ID for the asset.
         * @param {string} params.filter - Filter key.
         * @param {string} params.value - Filter value.
         */
        setAssetFilter({ facilityId, assetId, filter, value }) {
            if (!(facilityId in this.filters)) {
                this.filters[facilityId] = buildDefaultFacilityFilters();
            }
            if (!(assetId in this.filters[facilityId]['assets'])) {
                this.filters[facilityId]['assets'][assetId] =
                    buildInitialFilterStructure();
            }
            this.filters[facilityId]['assets'][assetId][filter] = value;
        },

        /**
         * Stores a filter applied at the user account level.
         * @param {Object} params - Filter parameters.
         * @param {string} params.filter - Filter key.
         * @param {string} params.value - Filter value.
         */
        setUserAccountFilter({ filter, value }) {
            if (!('userAccount' in this.filters)) {
                this.filters['userAccount'] = buildInitialFilterStructure();
            }
            this.filters['userAccount'][filter] = value;
        }
    }
});
